import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import { SensorDeviceModel } from 'src/types/dashboardSam';
// eslint-disable-next-line import/no-named-as-default
import signalRService from 'src/components/settings/drawer/signalR.service';
import { useSnackbar } from 'src/components/snackbar';
import { CardHeader } from '@mui/material';

// ----------------------------------------------------------------------

interface Props {
  sensorDevices: SensorDeviceModel[];
}

interface ThermometerProps {
  value: number;
  max: number;
  color: string;
  label: string;
}

interface GaugeCircleProps {
  value: number;
  color: string;
  label: string;
}

// ----------------------------------------------------------------------

// Thermometer Component
function Thermometer({ value, max, color, label }: ThermometerProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100); 

  return (
    <Stack alignItems="center" spacing={1}>
      <Box sx={{ position: 'relative', width: 40, height: 180 }}>
        {/* Thermometer body */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 20,
            height: 145,
            bgcolor: alpha(color, 0.1),
            borderRadius: '10px 10px 0 0',
            border: `2px solid ${alpha(color, 0.3)}`,
          }}
        >
          {/* Fill */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: `${percentage}%`,
              bgcolor: color,
              borderRadius: '8px 8px 0 0',
              transition: 'height 0.5s ease',
            }}
          />
        </Box>

        {/* Bulb */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: color,
            border: `3px solid ${alpha(color, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
            {value.toFixed(0)}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', textAlign: 'center', whiteSpace: 'pre-line' }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

// Gauge Circle Component
function GaugeCircle({ value, color, label }: GaugeCircleProps) {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const normalizedValue = Math.min(Math.max(value, 0), 100);
  const offset = circumference - (normalizedValue / 100) * circumference;
  const strokeWidth = 15;
  const size = radius * 2 + strokeWidth;
 
  return (
    <Stack alignItems="center" spacing={1}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={alpha(color, 0.1)}
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>

        <Stack
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
            {normalizedValue.toFixed(0)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            %
          </Typography>
        </Stack>
      </Box>

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', textAlign: 'center', whiteSpace: 'pre-line' }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function AppSensorThermometer({ sensorDevices }: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedDeviceId, setSelectedDeviceId] = useState<number | ''>('');
  const [devices, setDevices] = useState<SensorDeviceModel[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isConnected, setIsConnected] = useState(false);
  // Initialize devices and selected device
  useEffect(() => {
    if (sensorDevices && sensorDevices.length > 0) {
      setDevices(sensorDevices);
      if (!selectedDeviceId && sensorDevices[0]?.deviceId) {
        setSelectedDeviceId(sensorDevices[0].deviceId);
      }
    }
  }, [sensorDevices, selectedDeviceId]);

  // Get selected device and parse sensors
  const { selectedDevice, parsedSensors } = useMemo(() => {
    const device = devices.find((d) => d.deviceId === selectedDeviceId);
    const sensors = device?.sensors || [];

    const parsed = sensors.map((sensor) => {
      let values: Record<string, number> = {};
      try {
        values = JSON.parse(sensor.jValue || '{}');
      } catch (error) {
        console.error('Failed to parse sensor values:', error);
      }
      return {
        ...sensor, 
        values,
      };
    });

    return { selectedDevice: device, parsedSensors: parsed };
  }, [devices, selectedDeviceId]);

  // Extract sensor values
  const sensorValues = useMemo(() => {
    const temperatureSensor = parsedSensors.find((s) =>
      s.sensorType?.includes('Temperature')
    );
    const analogSensor = parsedSensors.find((s) => s.sensorType?.includes('A1'));
    const wet = analogSensor?.minValueSS ?? 0;
    const dry = analogSensor?.maxValueSS ?? 0;
    return {
      temperature: temperatureSensor?.values.Temperature ?? 0,
      humidity: temperatureSensor?.values.Humidity ?? 0,
      dewPoint: temperatureSensor?.values.DewPoint ?? 0,
      soilMoisture: analogSensor?.values.A1 ?? 0,
      wetValue: wet, // Trả về wetValue
      dryValue: dry,
    };
  }, [parsedSensors]);

  // Color functions
  const getTemperatureColor = useCallback(
    (temp: number) => {
      if (temp < 15) return theme.palette.info.main;
      if (temp < 25) return theme.palette.success.main;
      if (temp < 30) return theme.palette.warning.main;
      return theme.palette.error.main;
    },
    [theme]
  );

  const getHumidityColor = useCallback(
    (hum: number) => {
      if (hum < 40) return theme.palette.warning.main;
      if (hum < 70) return theme.palette.success.main;
      return theme.palette.info.main;
    },
    [theme]
  );

  const getSoilColor = useCallback(
    (soil: number) => {
      const normalized = Math.min((soil / 400) * 100, 100);
      if (normalized < 30) return theme.palette.error.main;
      if (normalized < 50) return theme.palette.warning.main;
      if (normalized < 75) return theme.palette.success.main;
      return theme.palette.info.main;
    },
    [theme]
  );

  // Format last update time
  const formatLastUpdate = useCallback(() => {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);
    
    if (seconds < 60) return `${seconds}s trước`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}p trước`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h trước`;
    
    const days = Math.floor(hours / 24);
    return `${days}d trước`;
  }, [lastUpdate]);

  // SignalR connection and listeners
  useEffect(() => {
    setIsConnected(signalRService.isConnected());

    const unsubscribeSensor = signalRService.onSensorReading(
      (sensorDevice: SensorDeviceModel) => {

        setDevices((prevDevices) => {
          // Initialize if no devices 
          if (!prevDevices || prevDevices.length === 0) {
            return [sensorDevice];
          }

          const index = prevDevices.findIndex(
            (d) => d.deviceId === sensorDevice.deviceId
          );

          if (index !== -1) {
            // Update existing device
            const updated = [...prevDevices];
            updated[index] = sensorDevice;
            if(process.env.NODE_ENV === "development") console.log('✅ Updated device:', updated[index].deviceId); 
            return updated;
          }

          return [...prevDevices, sensorDevice];
        });

        setLastUpdate(new Date());
      }
    );

    return () => {
      unsubscribeSensor();
    };
  }, [enqueueSnackbar]);

  // Auto refresh time display
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update time display
      setLastUpdate((prev) => new Date(prev));
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Handle device selection change
  const handleDeviceChange = useCallback((deviceId: number) => {
    setSelectedDeviceId(deviceId);
  }, []);

  return (
    <Card sx={{ alignItems: 'center' ,paddingBottom:2}}>
      <Stack spacing={3}>
        {/* Header with device selector */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
        >
          <CardHeader
            title="Giám sát cảm biến" 
            subheader={`${selectedDevice?.deviceName ?? ''} - Cập nhật: ${formatLastUpdate()}`}
          />
       
          {devices.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200, pr:2,pl:2 }}>
              <InputLabel sx={{pl:2}} id="device-select-label">Chọn thiết bị</InputLabel>
              <Select  
                labelId="device-select-label"
                id="device-select"
                value={selectedDeviceId}
                label="Chọn thiết bị"
                onChange={(e) => handleDeviceChange(e.target.value as number)}
              >
                {devices.map((device) => (
                  <MenuItem key={device.deviceId} value={device.deviceId}>
                    {device.deviceName || device.deviceCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        {/* Sensors display */}
        {/* eslint-disable-next-line no-nested-ternary */}
        {devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Không có thiết bị nào
            </Typography>
          </Box>
        ) : !selectedDevice ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Vui lòng chọn thiết bị
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            <Grid xs={6} sm={3}>
              <Thermometer
                value={sensorValues.temperature}
                max={100}
                color={getTemperatureColor(sensorValues.temperature)}
                label={`Nhiệt độ\n${sensorValues.temperature.toFixed(1)}°C`}
              />
            </Grid>

            <Grid xs={6} sm={3}>
              <GaugeCircle
                value={sensorValues.humidity}
                color={getHumidityColor(sensorValues.humidity)}
                label={`Độ ẩm KK\n${sensorValues.humidity.toFixed(1)}%`}
              />
            </Grid>

            <Grid xs={6} sm={3}>
              <GaugeCircle
                value={(sensorValues.dryValue - sensorValues.soilMoisture) * 100 / (sensorValues.dryValue - sensorValues.wetValue)}
                color={getSoilColor(sensorValues.soilMoisture)}
                label={`Độ ẩm đất\n${sensorValues.soilMoisture}`}
              />
            </Grid>

            <Grid xs={6} sm={3}>
              <Thermometer
                value={sensorValues.dewPoint}
                max={100}
                color={theme.palette.primary.main}
                label={`Điểm sương\n${sensorValues.dewPoint.toFixed(1)}°C`}
              />
            </Grid>
          </Grid>
        )}
      </Stack>
    </Card>
  );
}